import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Search, X } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { ARTICLES } from "../data/articles";
import { TOPICS, IMAGES } from "../data/content";

const CATEGORIES = ["All", ...TOPICS.map((t) => t.name)];

export default function Insights() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const list = useMemoList(category, q);

  const setCategory = (c) => {
    if (c === "All") params.delete("category");
    else params.set("category", c);
    setParams(params, { replace: true });
  };

  return (
    <div>
      <PageHero
        eyebrow="Insight Blogs"
        lines={["For the back", "of your mind."]}
        sub="Short, honest reads on anxiety, burnout, self-compassion and mindfulness — written from real sessions, not textbooks."
        image={IMAGES.journalFlatlay}
        alt="A calm journal and notebook flatlay"
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
                    category === c
                      ? "border-pine bg-pine text-cream"
                      : "border-pine/20 bg-white text-bodytext hover:border-pine/50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative lg:w-80">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
              <input
                data-testid="blog-search-input"
                className="input-soft pl-11"
                placeholder="Search the insights…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search articles"
              />
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
          {list.length === 0 && (
            <div data-testid="blog-empty-state" className="col-span-full py-20 text-center">
              <p className="font-serif text-2xl italic text-ink">Nothing under that rock yet.</p>
              <p className="mt-2 text-sm text-bodytext">Try another word or category.</p>
            </div>
          )}

          {list.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.07}>
              <button
                data-testid={`article-card-${i}`}
                onClick={() => setOpen(a)}
                className="card-soft group flex h-full w-full flex-col p-8 text-left"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-terra">
                  {a.category}
                </p>
                <h3 className="mt-4 flex-1 font-serif text-2xl leading-snug text-ink">
                  {a.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-bodytext/85 line-clamp-3">
                  {a.excerpt}
                </p>
                <p className="mt-6 flex items-center justify-between text-xs text-bodytext/60">
                  <span>{a.read_time} read</span>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-pine/20 text-pine transition-all duration-300 group-hover:bg-pine group-hover:text-cream">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </p>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label={open.title}
          >
            <div
              data-testid="article-modal-backdrop"
              className="absolute inset-0 bg-pinedeep/60 backdrop-blur-sm"
              onClick={() => setOpen(null)}
            />
            <motion.article
              data-testid="article-modal"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-t-[2rem] bg-cream p-8 shadow-2xl sm:rounded-[2rem] sm:p-12"
            >
              <button
                data-testid="article-modal-close"
                onClick={() => setOpen(null)}
                aria-label="Close article"
                className="absolute right-6 top-6 rounded-full border border-pine/15 p-2 text-pine hover:bg-sagelight/60"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-terra">
                {open.category} · {open.read_time} read
              </p>
              <h2 className="mt-4 font-serif text-3xl leading-tight text-ink sm:text-4xl">
                {open.title}
              </h2>
              <p className="mt-3 text-xs uppercase tracking-[0.16em] text-bodytext/60">
                {open.author} · {open.published_at}
              </p>
              <p className="mt-8 border-l-2 border-terra/50 pl-5 font-serif text-xl italic leading-relaxed text-pine">
                {open.excerpt}
              </p>
              <div className="mt-8 space-y-5">
                {open.content.map((p, i) => (
                  <p key={i} className="leading-relaxed text-bodytext">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-10 rounded-2xl bg-sagelight/50 p-5 text-sm text-bodytext">
                If this one landed close to home, a <strong>free intro call</strong> is a
                kind next step.
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function useMemoList(category, q) {
  const needle = q.trim().toLowerCase();
  return useMemo(
    () =>
      ARTICLES.filter(
        (a) =>
          (category === "All" || a.category === category) &&
          (!needle ||
            a.title.toLowerCase().includes(needle) ||
            a.excerpt.toLowerCase().includes(needle) ||
            a.category.toLowerCase().includes(needle))
      ),
    [category, needle]
  );
}
